import { useMutation } from '@apollo/react-hooks';
import {
    Autocomplete,
    Button,
    Grid,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { debounce } from '@mui/material/utils';
import React, { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { CREATE_ACCOUNT_CHANNEL_SUBSCRIPTION } from '../../../gql/subscriptions';
import { ChannelSubscribeInput } from '../../../gql/users';
import { Channel, ChannelSubscriptionType } from '../../../shared/types';
import { ContactMarexEmail } from './ContactMarexEmail';
import { SelectOption } from './PremiumChannelPaymentScreen';
import TermsAndConditionsCheckbox from '../../ui/TermsAndConditionsCheckbox';
import { useFormControl, useFormGroup } from '../../../utils/form.utils';
import {
    booleanTrueValidator,
    requiredValidator,
} from '../../../utils/validator.utils';

interface PremiumChannelAccountPaymentProps {
    billingAccounts: SelectOption[];
    channelId: string;
    fetchBillingAccounts: (
        filter: string,
        callback: (options: SelectOption[]) => void
    ) => void;
    successUrl: string;
    channelName: string;
    goBack: () => void;
}

export const PremiumChannelAccountPayment = (
    props: PremiumChannelAccountPaymentProps
) => {
    const {
        billingAccounts,
        channelId,
        successUrl,
        channelName,
        fetchBillingAccounts,
        goBack,
    } = props;
    const [accountOptions, setAccountOptions] =
        useState<SelectOption[]>(billingAccounts);

    const form = useFormGroup({
        accountNumber: useFormControl<SelectOption | null>(null, [
            requiredValidator,
        ]),
        acceptedTerms: useFormControl<boolean>(false, [booleanTrueValidator]),
    });

    const history = useHistory();

    const [createAccountChannelSubscription] = useMutation<
        Channel,
        { item: ChannelSubscribeInput }
    >(CREATE_ACCOUNT_CHANNEL_SUBSCRIPTION, {
        onCompleted: () => {
            history.replace(successUrl);
        },
    });

    const onSubmit = async (ev: any) => {
        if (!channelId) return;

        ev?.preventDefault();
        form.setDirty();
        if (!form.isValid) {
            return;
        }

        return createAccountChannelSubscription({
            variables: {
                item: {
                    channelId: channelId,
                    subscriptionType: ChannelSubscriptionType.Account,
                    accountNumber: form.value.accountNumber,
                },
            },
        });
    };

    const debounceGetUser = useMemo(
        () =>
            debounce(
                (
                    filter: string,
                    callback: (options: SelectOption[]) => void
                ) => {
                    fetchBillingAccounts(filter, callback);
                },
                500
            ),
        [fetchBillingAccounts]
    );

    useEffect(() => {
        setAccountOptions(billingAccounts);
    }, [billingAccounts]);

    return (
        <Grid container spacing={4}>
            <Grid size={12}>
                <Stack spacing={2}>
                    <Typography variant="body1">
                        You will be charged every 1st of the month. This
                        subscription will appear on your monthly statement as{' '}
                        <strong>Neon Insights - {channelName}</strong>.
                    </Typography>
                    <Autocomplete<SelectOption>
                        id="clearing-account-select-id"
                        options={accountOptions}
                        value={form.accountNumber.value}
                        isOptionEqualToValue={(option, value) =>
                            option.value === value.value
                        }
                        getOptionLabel={(option) => option.label}
                        onBlur={form.accountNumber.handleBlur}
                        onChange={(_, option): void => {
                            form.accountNumber.setValue(option);
                        }}
                        onInputChange={(_, inputValue, reason) => {
                            if (reason === 'input') {
                                debounceGetUser(inputValue, setAccountOptions);
                            }
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Clearing account number"
                                placeholder="Select an account"
                                error={form.accountNumber.showError}
                                helperText={
                                    form.accountNumber.showError
                                        ? form.accountNumber.validationMessage
                                        : undefined
                                }
                            />
                        )}
                    />
                    <Typography
                        variant="body2"
                        sx={{
                            color: 'text.secondary',
                        }}
                    >
                        If the account is not listed, please contact{' '}
                        <ContactMarexEmail /> and supply the correct account
                        number.
                    </Typography>
                </Stack>
            </Grid>
            <Grid size={12}>
                <TermsAndConditionsCheckbox
                    acceptedTerms={form.acceptedTerms.value}
                    setAcceptedTerms={(acceptedTerms: boolean) =>
                        form.acceptedTerms.setValue(acceptedTerms)
                    }
                    showError={form.acceptedTerms.showError}
                />
            </Grid>
            <Grid size={12}>
                <Stack direction="row" spacing={1}>
                    <Button
                        variant="outlined"
                        color="secondary"
                        onClick={goBack}
                    >
                        Back
                    </Button>
                    <Button variant="contained" onClick={onSubmit}>
                        Confirm
                    </Button>
                </Stack>
            </Grid>
        </Grid>
    );
};
